import { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Drawer, Input, Spin, Typography } from 'antd';
import {
  CheckOutlined,
  ClusterOutlined,
  LockOutlined,
  PlayCircleOutlined,
  RobotOutlined,
  SearchOutlined,
  ShopOutlined,
  SwapOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { getAgentById } from '@/api/agents';
import { getRunPreview, type RunPreview } from '@/api/runEvidence';
import { getAgentModel, type AgentModelState } from '@/api/agentModel';
import { EvaluationModelRole, getModelPlan, type EvaluationModelRoleView } from '@/api/scenarioIntelligence';
import { modelLine } from '@/components/ModelPickerDrawer';
import { ROLE_USE } from './EvaluationModelsDrawer';
import type { MemberSearchHit } from '@/api/briefingContext';
import { searchScenarios } from '@/api/scenarios';
import type { AgentDefinition } from '@/api/types';
import { newRequestKey } from '@/api/validationRuns';
import { AgentPickerDrawer } from '@/components/AgentPickerDrawer';
import { PatientPickerDrawer } from '@/components/PatientPickerDrawer';
import { StatusPill } from '@/components/StatusPill';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { CostSection, HowItRuns, ScenariosToRun } from './RunDrawerSections';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// RunTargetDrawer — EVAL-06 D-E2 + review R1/R10/§4. Where a validation run acts, chosen the way the founder set it
// (31 October): practice first, then patient and agent. A run tests the agent that belongs to its practice, so an
// agent of one practice runs only there (shown, not chosen); a shared agent can run at any practice. Patient search is
// PatientPickerDrawer (the shared clinician picker in a stacked drawer); agent search is AgentPickerDrawer, limited to
// the agents of the run's practice. The server enforces the same rule and refuses a run that breaks it.
// ─────────────────────────────────────────────────────────────────────────────

/** Where a validation run acts. Empty = every scenario as written. */
export interface RunTarget {
  merchantId?: number;
  memberId?: number;
}

export interface RunTargetRequest {
  agentId: number;
  target: RunTarget;
  /** One per intended run: kept across a failed submit, new when the agent, practice or patient changes (review R10). */
  key: string;
}

const MAX_PRACTICE_ROWS = 8;

function practiceOf(agent: AgentDefinition | null | undefined): number | null {
  return agent?.merchantId != null && agent.merchantId > 0 ? agent.merchantId : null;
}

function patientName(p: MemberSearchHit): string {
  return [p.firstName, p.middleName, p.lastName].filter(Boolean).join(' ').trim() || `Patient #${p.id}`;
}

function Step({ n, label, summary }: { n: number; label: string; summary?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
      <span
        style={{
          width: 22,
          height: 22,
          borderRadius: '50%',
          background: colors.ink[600],
          color: '#FFFFFF',
          fontSize: 12,
          fontWeight: 600,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {n}
      </span>
      <Text strong style={{ fontSize: 14 }}>{label}</Text>
      {summary && <span style={{ marginLeft: 'auto', minWidth: 0 }}>{summary}</span>}
    </div>
  );
}

/** One choice in a step, styled like the AgentPickerDrawer / MerchantPickerDrawer rows. */
function Row({
  isDark,
  selected,
  onClick,
  icon,
  title,
  sub,
}: {
  isDark: boolean;
  selected: boolean;
  onClick?: () => void;
  icon: React.ReactNode;
  title: React.ReactNode;
  sub?: React.ReactNode;
}) {
  const subColor = isDark ? colors.slate[400] : colors.slate[500];
  // A row with nothing to choose (the agent's own practice, the scenario's agent) is not a button at all.
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        padding: '9px 12px',
        border: 'none',
        borderLeft: selected ? `3px solid ${colors.ink[600]}` : '3px solid transparent',
        background: selected ? (isDark ? colors.ink[700] + '33' : colors.ink[50]) : 'transparent',
        cursor: onClick ? 'pointer' : 'default',
        textAlign: 'left',
        borderRadius: 6,
      }}
    >
      <span style={{ color: subColor, fontSize: 15 }}>{icon}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: isDark ? colors.slate[50] : colors.slate[900] }}>
          {title}
        </span>
        {sub && <span style={{ display: 'block', fontSize: 11.5, color: subColor, marginTop: 2 }}>{sub}</span>}
      </span>
      {selected && <CheckOutlined style={{ color: colors.ink[600] }} />}
    </Tag>
  );
}

export const RunTargetDrawer = observer(function RunTargetDrawer({
  open,
  onClose,
  agentId: initialAgentId,
  scenarioId,
  onRun,
}: {
  open: boolean;
  onClose: () => void;
  /** The agent whose scenarios run. */
  agentId: number;
  /** One scenario (its agent is fixed); omitted = every active scenario of the agent (the agent can be changed). */
  scenarioId?: string;
  /** Submits the run: true once the server has the job (the drawer closes), false keeps it, and its key, for a retry. */
  onRun: (request: RunTargetRequest) => Promise<boolean>;
}) {
  const { themeStore, merchantContextStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  const [agentId, setAgentId] = useState(initialAgentId);
  const [agent, setAgent] = useState<AgentDefinition | null | undefined>(undefined);
  const [chosenPractice, setChosenPractice] = useState<number | undefined>();
  const [practiceQuery, setPracticeQuery] = useState('');
  const [patient, setPatient] = useState<MemberSearchHit | null>(null);
  const [patientPickerOpen, setPatientPickerOpen] = useState(false);
  const [agentPickerOpen, setAgentPickerOpen] = useState(false);
  const [scenarioCount, setScenarioCount] = useState<number | null>(null);
  // D-SI-4 / D-SI-9: the models a run involves, shown, not chosen here.
  const [agentModel, setAgentModel] = useState<AgentModelState | null | undefined>(undefined);
  const [evaluator, setEvaluator] = useState<EvaluationModelRoleView | null | undefined>(undefined);
  // SI-03b: what the server says this run would do — the scenarios (drafts marked Exploratory) and a cost estimate.
  const [preview, setPreview] = useState<RunPreview | null | undefined>(undefined);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const keyRef = useRef<{ signature: string; key: string } | null>(null);

  // A fresh drawer starts from the caller's agent, as written.
  useEffect(() => {
    if (!open) return;
    setAgentId(initialAgentId);
    setChosenPractice(undefined);
    setPracticeQuery('');
    setPatient(null);
    keyRef.current = null;
    if (merchantContextStore.merchants.length === 0 && !merchantContextStore.isLoading) void merchantContextStore.loadMerchants();
  }, [open, initialAgentId, merchantContextStore]);

  // The agent's own practice decides where a run can act (review R1). The row the picker handed back answers at once;
  // otherwise the agent is read by id (it may belong to another practice than the one selected).
  const picked = useRef<AgentDefinition | null>(null);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    if (picked.current?.id === agentId) setAgent(picked.current);
    else {
      setAgent(undefined);
      getAgentById(agentId)
        .then((a) => !cancelled && setAgent(a))
        .catch(() => !cancelled && setAgent(null)); // unknown: any practice; the server refuses a wrong one and says why
    }
    return () => {
      cancelled = true;
    };
  }, [open, agentId]);

  // The agent's own model and the plan's Evaluator, for the Models section. A failed read says so; it never blocks a run.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setAgentModel(undefined);
    getAgentModel(agentId)
      .then((m) => !cancelled && setAgentModel(m))
      .catch(() => !cancelled && setAgentModel(null));
    return () => {
      cancelled = true;
    };
  }, [open, agentId]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setEvaluator(undefined);
    const practiceId = merchantContextStore.selectedMerchantId;
    if (practiceId == null) {
      setEvaluator(null);
      return;
    }
    getModelPlan(practiceId)
      .then((p) => !cancelled && setEvaluator(p.roles.find((r) => r.role === EvaluationModelRole.Evaluator) ?? null))
      .catch(() => !cancelled && setEvaluator(null));
    return () => {
      cancelled = true;
    };
  }, [open, merchantContextStore.selectedMerchantId]);

  useEffect(() => {
    if (!open) return;
    const request = new AbortController();
    setPreview(undefined);
    setPreviewError(null);
    getRunPreview(agentId, scenarioId, request.signal)
      .then((p) => !request.signal.aborted && setPreview(p))
      .catch((err: Error) => {
        if (request.signal.aborted) return;
        setPreview(null);
        setPreviewError(err.message);
      });
    return () => request.abort();
  }, [open, agentId, scenarioId]);

  // What "Run all" costs: every active scenario of the chosen agent.
  useEffect(() => {
    if (!open || scenarioId) return;
    let cancelled = false;
    setScenarioCount(null);
    searchScenarios({ agentId })
      .then((list) => !cancelled && setScenarioCount(list.length))
      .catch(() => !cancelled && setScenarioCount(null));
    return () => {
      cancelled = true;
    };
  }, [open, scenarioId, agentId]);

  const ownPractice = practiceOf(agent);
  const practice = ownPractice ?? chosenPractice;
  const practiceRow = practice != null ? merchantContextStore.merchants.find((m) => m.id === practice) : undefined;
  const practiceLabel = practice != null ? `${practiceRow?.title ?? 'Practice'} · #${practice}` : null;

  const practiceRows = useMemo(() => {
    const q = practiceQuery.trim().toLowerCase();
    return merchantContextStore.merchants
      .filter((m) => !q || `${m.title} ${m.codeName ?? ''} ${m.city ?? ''} ${m.id}`.toLowerCase().includes(q))
      .slice(0, MAX_PRACTICE_ROWS);
  }, [merchantContextStore.merchants, practiceQuery]);

  // Practice first: a patient belongs to one practice, so a different practice clears the patient.
  const choosePractice = (id: number | undefined) => {
    if (id === chosenPractice) return;
    setChosenPractice(id);
    setPatient(null);
  };

  const target: RunTarget =
    ownPractice != null
      ? { merchantId: patient ? ownPractice : undefined, memberId: patient?.id }
      : { merchantId: chosenPractice, memberId: chosenPractice != null ? patient?.id : undefined };

  const run = async () => {
    if (submitting) return;
    const signature = `${agentId}|${target.merchantId ?? ''}|${target.memberId ?? ''}`;
    if (keyRef.current?.signature !== signature) keyRef.current = { signature, key: newRequestKey() };
    setSubmitting(true);
    try {
      if (await onRun({ agentId, target, key: keyRef.current.key })) {
        keyRef.current = null;
        onClose();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const headerBg = isDark ? colors.slate[800] : '#FFFFFF';
  const bodyBg = isDark ? colors.slate[900] : colors.slate[50];
  const borderColor = isDark ? colors.slate[700] : colors.slate[200];

  const card = { background: headerBg, border: `1px solid ${borderColor}`, borderRadius: 10, padding: 16 };
  const toRun = preview?.scenarios.length ?? scenarioCount;
  const runLabel = scenarioId
    ? 'Run this scenario'
    : toRun != null
      ? `Run ${toRun} scenario${toRun === 1 ? '' : 's'}`
      : 'Run every scenario';

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      size="min(560px, 100vw)"
      closable={false}
      title={null}
      styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column' }, header: { display: 'none' } }}
    >
      {/* Header */}
      <div style={{ padding: '16px 20px 12px', borderBottom: `1px solid ${borderColor}`, background: headerBg, display: 'flex', gap: 12 }}>
        <PlayCircleOutlined style={{ fontSize: 18, color: colors.ink[600], marginTop: 3 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text strong style={{ fontSize: 15, display: 'block' }}>
            {scenarioId ? 'Run this scenario for a chosen patient' : 'Run every scenario for a chosen patient'}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Practice first, then patient and agent. A run tests the agent that belongs to its practice.
          </Text>
        </div>
        <Button size="small" onClick={onClose}>
          Close
        </Button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', background: bodyBg, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* 1 · Practice */}
        <section style={card}>
          <Step n={1} label="Practice" summary={ownPractice != null ? <StatusPill tone="slate">Set by the agent</StatusPill> : undefined} />
          {agent === undefined ? (
            <Spin size="small" />
          ) : ownPractice != null ? (
            <Row
              isDark={isDark}
              selected
              icon={<ShopOutlined />}
              title={practiceLabel}
              sub="This agent's own practice. It runs only here, because a run tests the agent that belongs to its practice."
            />
          ) : (
            <>
              <Input
                allowClear
                prefix={<SearchOutlined style={{ color: colors.slate[400] }} />}
                placeholder="Search practices by name, code or city…"
                value={practiceQuery}
                onChange={(e) => setPracticeQuery(e.target.value)}
                style={{ marginBottom: 8 }}
              />
              <Row
                isDark={isDark}
                selected={chosenPractice == null}
                onClick={() => choosePractice(undefined)}
                icon={<ShopOutlined />}
                title="As written"
                sub="Each scenario runs at the practice it was written for."
              />
              {merchantContextStore.isLoading && merchantContextStore.merchants.length === 0 && <Spin size="small" />}
              {practiceRows.map((m) => (
                <Row
                  isDark={isDark}
                  key={m.id}
                  selected={chosenPractice === m.id}
                  onClick={() => choosePractice(m.id)}
                  icon={<ShopOutlined />}
                  title={`${m.title} · #${m.id}`}
                  sub={[m.codeName, m.city, m.province].filter(Boolean).join(' · ') || undefined}
                />
              ))}
            </>
          )}
        </section>

        {/* 2 · Patient */}
        <section style={card}>
          <Step n={2} label="Patient" summary={<Text type="secondary" style={{ fontSize: 12 }}>optional</Text>} />
          {practice == null ? (
            <Text type="secondary" style={{ fontSize: 12.5 }}>
              Each scenario uses the patient it was written for. Choose a practice to run them for one patient of it.
            </Text>
          ) : (
            <>
              <Row
                isDark={isDark}
                selected={patient == null}
                onClick={() => setPatient(null)}
                icon={<UserOutlined />}
                title="Each scenario's own patient"
                sub="As written."
              />
              {patient ? (
                <Row
                  isDark={isDark}
                  selected
                  onClick={() => setPatientPickerOpen(true)}
                  icon={<UserOutlined />}
                  title={patientName(patient)}
                  sub={
                    <span>
                      {`#${patient.id} · used by every scenario, including those that name no patient · `}
                      <span style={{ color: colors.ink[600], fontWeight: 600 }}>Change</span>
                    </span>
                  }
                />
              ) : (
                <Button icon={<UserOutlined />} onClick={() => setPatientPickerOpen(true)} style={{ marginTop: 8 }}>
                  Choose a patient…
                </Button>
              )}
              <Text type="secondary" style={{ display: 'block', fontSize: 11.5, marginTop: 8 }}>
                The patient must belong to {practiceLabel}; the server checks it before anything runs.
              </Text>
            </>
          )}
        </section>

        {/* 3 · Agent */}
        <section style={card}>
          <Step
            n={3}
            label="Agent"
            summary={
              !scenarioId ? (
                <Button size="small" icon={<SwapOutlined />} onClick={() => setAgentPickerOpen(true)}>
                  Change agent
                </Button>
              ) : (
                <StatusPill tone="slate">This scenario's agent</StatusPill>
              )
            }
          />
          <Row
            isDark={isDark}
            selected
            onClick={scenarioId ? undefined : () => setAgentPickerOpen(true)}
            icon={<RobotOutlined />}
            title={agent?.displayName || `Agent #${agentId}`}
            sub={
              <>
                {agent?.canonicalName && (
                  <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{agent.canonicalName}</span>
                )}
                {agent?.canonicalName && ' · '}
                {ownPractice != null ? `of ${practiceLabel}` : 'shared by every practice'}
                {scenarioId ? ` · scenario ${scenarioId}` : scenarioCount != null ? ` · ${scenarioCount} active scenarios` : ''}
              </>
            }
          />
        </section>

        {/* 4 · Scenarios — what the server would run, drafts marked Exploratory (SI-03b). */}
        <section style={card}>
          <Step
            n={4}
            label="Scenarios"
            summary={
              preview ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {preview.scenarios.length} to run
                </Text>
              ) : undefined
            }
          />
          <ScenariosToRun preview={preview} error={previewError} isDark={isDark} />
        </section>

        {/* 5 · How it runs — one mode, one trial: said, not offered (SI-03 brings the choices). */}
        <section style={card}>
          <Step n={5} label="How it runs" summary={<StatusPill tone="slate">Shown, not chosen here</StatusPill>} />
          <HowItRuns />
        </section>

        {/* 6 · Models — shown, not chosen: the agent runs on its own model (D-SI-4); the Evaluator is a setting (D-SI-9). */}
        <section style={card}>
          <Step n={6} label="Models" summary={<StatusPill tone="slate">Shown, not chosen here</StatusPill>} />
          <Row
            isDark={isDark}
            selected={false}
            icon={<LockOutlined />}
            title={
              agentModel === undefined
                ? 'Reading the agent’s model…'
                : agentModel
                  ? `Runs on ${[agentModel.deploymentName ?? agentModel.modelName, agentModel.apiTitle].filter(Boolean).join(' · ')}, from agent configuration`
                  : 'The agent’s model could not be read'
            }
            sub="Not selectable here. To test another model, create a candidate version. Every model round records the deployment that actually answered."
          />
          <Row
            isDark={isDark}
            selected={false}
            icon={<ClusterOutlined />}
            title={
              evaluator === undefined
                ? 'Reading the Evaluator…'
                : evaluator?.deployment
                  ? `Evaluator: ${modelLine(evaluator.row) ?? evaluator.deployment}${evaluator.qualification ? ` · ${evaluator.qualification.toLowerCase()}` : ''}`
                  : `Evaluator: ${evaluator?.problem ?? 'not set'}`
            }
            sub={`${ROLE_USE[EvaluationModelRole.Evaluator]} Changed under Scenarios → Evaluation models.`}
          />
          <Text type="secondary" style={{ display: 'block', fontSize: 11.5, marginTop: 6 }}>
            Peers and the skill router run on their own configured models.
          </Text>
        </section>

        {/* 7 · Cost — an estimate from earlier recorded results; what cannot be priced is named, never $0 (SI-03b). */}
        <section style={card}>
          <Step n={7} label="Cost" summary={<Text type="secondary" style={{ fontSize: 12 }}>estimate</Text>} />
          <CostSection preview={preview} isDark={isDark} />
        </section>
      </div>

      {/* Footer */}
      <div style={{ padding: '12px 20px', borderTop: `1px solid ${borderColor}`, background: headerBg }}>
        {!scenarioId && (
          <Text type="secondary" style={{ display: 'block', fontSize: 11.5, marginBottom: 4 }}>
            Each scenario calls the model once per turn, so real credits are spent. The run happens in the background;
            follow it under Run requests, where it can be cancelled.
          </Text>
        )}
        {/* Review §4 (founder, 31 October: accepted). The peer sentence shows unless the agent is known not to consult. */}
        <Text type="secondary" style={{ display: 'block', fontSize: 11.5, marginBottom: 10 }}>
          A test run does not carry out the agent's tools that change a record or send something; the results show them as
          Not carried out.
          {agent?.enableConsultation !== false && ' An agent it consults may still create a file, which stays unused in storage.'}
        </Text>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" icon={<PlayCircleOutlined />} loading={submitting} disabled={agent === undefined} onClick={() => void run()}>
            {runLabel}
          </Button>
        </div>
      </div>

      {practice != null && (
        <PatientPickerDrawer
          open={patientPickerOpen}
          onClose={() => setPatientPickerOpen(false)}
          merchantId={practice}
          practiceLabel={practiceLabel ?? undefined}
          selectedId={patient?.id}
          onSelect={setPatient}
          title="Choose the patient for this run"
        />
      )}
      <AgentPickerDrawer
        open={agentPickerOpen}
        onClose={() => setAgentPickerOpen(false)}
        selectedId={agentId}
        allowClear={false}
        title={practiceLabel ? `Agents of ${practiceLabel}` : 'Shared agents'}
        // Practice first: only agents that belong to the run's practice, or are shared by every practice.
        filterFn={(a) => {
          const own = practiceOf(a);
          return own == null || own === practice;
        }}
        onSelect={(id, row) => {
          if (id == null || id === agentId) return;
          // The picker hands back the row it searched on the server.
          picked.current = row ?? null;
          // Moving to a shared agent keeps the practice already in use, so the patient stays valid.
          if (practiceOf(row) == null && practice != null) setChosenPractice(practice);
          setAgentId(id);
        }}
      />
    </Drawer>
  );
});
